import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { MdLock, MdPerson, MdSecurity, MdContentCopy, MdCheck, MdWarning } from 'react-icons/md';

function AdminLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // 'login', 'mfa', 'mfa-setup', 'mfa-verify'
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [recoveryCodesCopied, setRecoveryCodesCopied] = useState(false);
  const navigate = useNavigate();
  const { setToken } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      });

      if (response.data.mfa_setup_required) {
        // Admin needs to set up MFA first
        setStep('mfa-setup-init');
        await initiateMfaSetup();
      } else if (response.data.mfa_required) {
        // MFA is enabled, show OTP input
        setStep('mfa');
      } else if (response.data.access_token) {
        // Login successful
        await setToken(response.data.access_token);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const initiateMfaSetup = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/admin/mfa/setup-init`, {
        username,
        password
      });
      
      setMfaSetupData(response.data);
      setStep('mfa-setup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate MFA setup');
      setStep('login');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login/mfa`, {
        username,
        password,
        otp_code: otpCode
      });

      if (response.data.access_token) {
        await setToken(response.data.access_token);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/mfa/setup-verify`, {
        username,
        password,
        otp_code: otpCode
      });

      if (response.data.access_token) {
        await setToken(response.data.access_token);
        navigate('/admin/dashboard');
      } else {
        // MFA enabled but need to log in again
        setStep('mfa');
        setOtpCode('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    if (mfaSetupData?.recoveryCodes) {
      navigator.clipboard.writeText(mfaSetupData.recoveryCodes.join('\n'));
      setRecoveryCodesCopied(true);
      setTimeout(() => setRecoveryCodesCopied(false), 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-600/20 mb-4">
            <MdSecurity className="text-3xl text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white font-rubik">Admin Portal</h1>
          <p className="text-gray-400 mt-2">Secure administrator access</p>
        </div>

        {/* Login Form */}
        {step === 'login' && (
          <form onSubmit={handleLogin} className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                <div className="relative">
                  <MdPerson className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter admin username"
                    required
                    data-testid="admin-username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <MdLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter password"
                    required
                    data-testid="admin-password"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-start gap-2">
                  <MdWarning className="text-lg flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </button>
            </div>
          </form>
        )}

        {/* MFA Setup - Show QR Code */}
        {step === 'mfa-setup' && mfaSetupData && (
          <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/20 mb-4">
                <MdSecurity className="text-2xl text-yellow-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Set Up Two-Factor Authentication</h2>
              <p className="text-gray-400 text-sm mt-2">
                Admin accounts require 2FA for security. Scan the QR code with your authenticator app.
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-lg">
                <img 
                  src={mfaSetupData.qrCodeBase64} 
                  alt="MFA QR Code"
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* Manual Entry Secret */}
            <div className="mb-6 p-3 bg-gray-700 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Can't scan? Enter this code manually:</p>
              <code className="text-sm text-yellow-400 font-mono break-all">{mfaSetupData.secret}</code>
            </div>

            {/* Recovery Codes */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-300">Recovery Codes</h3>
                <button
                  onClick={copyRecoveryCodes}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"
                >
                  {recoveryCodesCopied ? <MdCheck className="text-green-400" /> : <MdContentCopy />}
                  {recoveryCodesCopied ? 'Copied!' : 'Copy all'}
                </button>
              </div>
              <div className="p-3 bg-gray-700 rounded-lg grid grid-cols-2 gap-2">
                {mfaSetupData.recoveryCodes?.map((code, i) => (
                  <code key={i} className="text-xs text-gray-300 font-mono">{code}</code>
                ))}
              </div>
              <p className="text-xs text-yellow-500 mt-2">
                ⚠️ Save these codes! They're your backup if you lose access to your authenticator.
              </p>
            </div>

            {/* Verification Form */}
            <form onSubmit={handleMfaSetupVerify}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Enter 6-digit code from authenticator app
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-2xl tracking-widest placeholder-gray-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="mfa-setup-code"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="w-full py-3 px-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                data-testid="mfa-setup-verify-btn"
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </form>
          </div>
        )}

        {/* MFA Verification (for users who already have MFA) */}
        {step === 'mfa' && (
          <form onSubmit={handleMfaVerify} className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/20 mb-4">
                <MdSecurity className="text-2xl text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Two-Factor Authentication</h2>
              <p className="text-gray-400 text-sm mt-2">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-3xl tracking-[0.5em] placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                placeholder="000000"
                maxLength={6}
                autoFocus
                data-testid="mfa-code"
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              data-testid="mfa-verify-btn"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('login');
                setOtpCode('');
                setError('');
              }}
              className="w-full mt-3 py-2 text-gray-400 hover:text-white text-sm"
            >
              ← Back to login
            </button>
          </form>
        )}

        {/* Loading state for MFA setup init */}
        {step === 'mfa-setup-init' && (
          <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-700 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-gray-300">Setting up two-factor authentication...</p>
          </div>
        )}

        {/* Security Note */}
        <p className="text-center text-gray-500 text-xs mt-6">
          This is a secure administrator portal. All access attempts are logged.
        </p>
      </div>
    </div>
  );
}

export default AdminLoginPage;
